import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Selector de estanque estandar para los filtros: `app-search-select` con
 * flechas para ir al estanque anterior / siguiente y atajo de teclado (← →).
 *
 * Se usa igual que `app-search-select` (formControlName + itemSelected); al
 * navegar con flechas tambien actualiza el control y emite `itemSelected` con
 * el estanque destino, asi la pagina recarga con su mismo handler.
 *
 * Uso:
 *   <app-pond-select [items]="ponds" [label]="'filter.pond' | translate"
 *     formControlName="pond_id" (itemSelected)="onPond($event)"></app-pond-select>
 */
@Component({
  selector: "app-pond-select",
  templateUrl: "./pond-select.html",
  styleUrls: ["./pond-select.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PondSelectComponent),
      multi: true
    }
  ]
})
export class PondSelectComponent implements ControlValueAccessor, OnChanges {
  /** Estanques disponibles, en el orden en que se navegan. */
  @Input() items: any[] = [];
  /** Etiqueta flotante del selector. */
  @Input() label = "";
  /** Texto cuando no hay estanque seleccionado. */
  @Input() placeholder = " ";
  /** Propiedad a mostrar de cada estanque. */
  @Input() displayKey = "name";
  /** Propiedad que se guarda como valor. */
  @Input() valueKey = "id";
  /** Habilita el atajo de teclado ← → (ignorado mientras se escribe en un campo). */
  @Input() keyboardNav = true;

  /** Estanque elegido (en el selector o con flechas); null al limpiar. */
  @Output() itemSelected = new EventEmitter<any>();

  /** Control interno que alimenta al app-search-select. */
  control = new FormControl(null);

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  /** true mientras se actualiza la lista de estanques (ver ngOnChanges). */
  private syncingItems = false;

  /**
   * Al cambiar la lista, app-search-select limpia su seleccion si el estanque
   * anterior ya no esta y emite null. Ese null no lo pidio el usuario: la pagina
   * ya decide el estanque de la lista nueva, asi que se ignora durante el cambio.
   * @param changes Cambios de inputs.
   * @returns void
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.items) return;
    this.syncingItems = true;
    Promise.resolve().then(() => (this.syncingItems = false));
  }

  /**
   * Posicion del estanque seleccionado dentro de `items`.
   * @returns Indice, o -1 si no hay estanque seleccionado.
   */
  private get currentIndex(): number {
    const value = this.control.value;
    if (value === null || value === undefined || value === "") return -1;
    return this.items.findIndex((item) => String(item[this.valueKey]) === String(value));
  }

  /**
   * Indica si hay un estanque anterior al seleccionado.
   * @returns true si se puede retroceder.
   */
  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Indica si hay un estanque siguiente al seleccionado.
   * @returns true si se puede avanzar.
   */
  get hasNext(): boolean {
    const index = this.currentIndex;
    return index !== -1 && index < this.items.length - 1;
  }

  /**
   * Selecciona el estanque contiguo al actual.
   * @param direction "next" para avanzar, "prev" para retroceder.
   * @returns void
   */
  navigate(direction: "next" | "prev"): void {
    const index = this.currentIndex;
    if (index === -1) return;
    const target = this.items[direction === "next" ? index + 1 : index - 1];
    if (!target) return;
    this.select(target);
  }

  /**
   * Recibe la seleccion del app-search-select interno.
   * @param item Estanque elegido o null al limpiar.
   * @returns void
   */
  onInnerSelected(item: any): void {
    if (!item && this.syncingItems) return;
    this.onChange(item ? item[this.valueKey] : "");
    this.onTouched();
    this.itemSelected.emit(item);
  }

  /**
   * Atajo de teclado ← → para cambiar de estanque.
   * @param event Evento de teclado.
   * @returns void
   */
  @HostListener("document:keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (!this.keyboardNav || this.items.length === 0) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const activeTag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
    if (activeTag === "input" || activeTag === "textarea" || activeTag === "select") return;
    this.navigate(event.key === "ArrowRight" ? "next" : "prev");
  }

  /**
   * Escribe el valor del formulario padre en el selector.
   * @param value Id del estanque.
   * @returns void
   */
  writeValue(value: any): void {
    this.control.setValue(value, { emitEvent: false });
  }

  /**
   * Registra el callback de cambio del formulario padre.
   * @param fn Callback.
   * @returns void
   */
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  /**
   * Registra el callback de "touched" del formulario padre.
   * @param fn Callback.
   * @returns void
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Habilita o deshabilita el selector.
   * @param isDisabled true para deshabilitar.
   * @returns void
   */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
      return;
    }
    this.control.enable({ emitEvent: false });
  }

  /**
   * Selecciona un estanque desde las flechas y lo propaga al formulario.
   * @param item Estanque destino.
   * @returns void
   */
  private select(item: any): void {
    this.control.setValue(item[this.valueKey], { emitEvent: false });
    this.onInnerSelected(item);
  }
}
